/**
 * Strips look: each video as a row, the whole video on the left and its five best moments on the
 * right, with the line that was said under each one. Shows what CherryClipz does before anything
 * is opened. A row asks for its clips only when it scrolls into view.
 */
import React, { useEffect, useRef, useState } from "react";
import { VideoData } from "../types";
import { formatDateOnly } from "../../../../utils/dateFormatting";
import { FAILED_TEXT } from "../constants/myLibraryText";
import Pic from "./Pic";
import { CARD } from "./libraryLookText";
import { clock, useTopClips } from "./topClips";
import { VideoCardProps } from "./videoCardProps";

const MOMENTS = 5;

const useSeen = (): [React.RefObject<HTMLDivElement>, boolean] => {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setSeen(true); }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
};

const StripRow: React.FC<Omit<VideoCardProps, "videos"> & { video: VideoData }> = ({
  video, onQuickLook, onOpenClips, onTop20, renderMenu, combineMode, picked, onTogglePick, isInFlight,
}) => {
  const inFlight = isInFlight(video);
  const failed = video.task_status === "failed";
  const ready = !inFlight && !failed;
  const [ref, seen] = useSeen();
  const { data, loading } = useTopClips(video.id, MOMENTS, "longest_quotes", seen && ready, String(video.thumbnails_ready !== false));
  const clips = data?.clips || [];
  const total = video.clips_count || 0;

  return (
    <div ref={ref} className={`strip${picked.has(video.id) ? " card picked" : ""}`}>
      <Pic src={inFlight ? null : video.thumbnail} videoUrl={inFlight ? null : video.video} onClick={() => (combineMode ? onTogglePick(video.id) : ready && onQuickLook(video))}>
        {video.duration_in_seconds ? <span className="dur">{clock(video.duration_in_seconds)}</span> : null}
        {inFlight && <span className="badge">{CARD.making}</span>}
        {combineMode && ready && (
          <span className="tick">
            <input type="checkbox" readOnly checked={picked.has(video.id)} style={{ accentColor: "#e61d50" }} />
            {picked.has(video.id) ? CARD.inSheet : CARD.addToSheet}
          </span>
        )}
      </Pic>
      <div style={{ minWidth: 0 }}>
        <div className="hd">
          <div className="ttl" title={video.title} onClick={() => ready && !combineMode && onQuickLook(video)}>{video.title}</div>
          {ready && !combineMode && (
            <>
              <button type="button" className="pill red" title={CARD.quickLookTitle} onClick={() => onQuickLook(video)}>{CARD.quickLook}</button>
              <button type="button" className="pill line" title={CARD.fullSheetTitle} onClick={() => onOpenClips(video)}>{CARD.fullSheet}</button>
              <button type="button" className="pill line" title={CARD.top20Title} onClick={() => onTop20(video)}>{CARD.top20}</button>
            </>
          )}
          {!combineMode && <div onClick={(e) => e.stopPropagation()}>{renderMenu(video)}</div>}
        </div>
        <div className="meta">
          {formatDateOnly(video.created_at)}
          {failed && <> · <span className="bad">{FAILED_TEXT}</span></>}
          {ready && total > 0 && <> · <b>{CARD.clips(total)}</b> · {CARD.bestMoments}:</>}
          {inFlight && <> · {CARD.makingNote}</>}
        </div>
        {inFlight && <div className="prog" style={{ maxWidth: 360 }}><i /></div>}
        {ready && (
          <div className="moments">
            {clips.length === 0 && (loading || !seen)
              ? Array.from({ length: MOMENTS }).map((_, i) => (
                <div key={i} className="mom"><div className="pic skel" /><p className="skel" style={{ height: 30, borderRadius: 6 }} /></div>
              ))
              : clips.map((clip) => (
                <div key={clip.id} className="mom" onClick={() => onQuickLook(video, clip.start_in, clip.id)} title={clip.text}>
                  <Pic src={clip.thumbnail}><span className="t">{clock(clip.start_in)}</span></Pic>
                  <p>“{clip.text || clip.label}”</p>
                </div>
              ))}
            {total > clips.length && clips.length > 0 && (
              <div className="moremom" onClick={() => onQuickLook(video)}>{CARD.more(total - clips.length)}</div>
            )}
            {!loading && seen && clips.length === 0 && <div className="meta">{CARD.noClipsYet}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

const StripsView: React.FC<VideoCardProps> = ({ videos, ...rest }) => (
  <div>
    {videos.map((video) => <StripRow key={video.id} video={video} {...rest} />)}
  </div>
);

export default StripsView;
